import { readCookie, writeCookie } from './cookies';

const consentCookieName = 'mutiny.pii.consent';
const optedInValue = 'opted_in';
const optedOutValue = 'opted_out';

type ConsentDecision = typeof optedInValue | typeof optedOutValue;
type ConsentListener = (piiTrackingEnabled: boolean) => void;

export type PiiConsentController = {
  // Resolves the initial PII state for a load: persisted visitor consent wins, otherwise the org
  // default (`defaultOptOutPiiTracking`) decides. Does not notify subscribers — it is the baseline.
  resolveEnabled: (defaultOptOutPiiTracking: boolean) => boolean;
  isPiiTrackingEnabled: () => boolean;
  // Notifies on runtime transitions only (e.g. a consent tool calling optIn/optOut mid-session).
  subscribe: (listener: ConsentListener) => () => void;
};

type InternalPiiConsentController = PiiConsentController & {
  attachWindowApi: () => void;
};

// Matches the repo-wide `window.mutiny` type (WindowMutiny resolves to `unknown`). Declared here too
// so this package compiles standalone; the identical `unknown` type avoids a merge conflict. We narrow
// with guards rather than a structured type. The consent tool only needs optIn/optOut.
declare global {
  interface Window {
    mutiny?: unknown;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function resolveIdentifyEmail(userIdOrEmail: unknown, info: unknown): string | null {
  const email = isRecord(info) && info.email !== undefined ? info.email : userIdOrEmail;
  return typeof email === 'string' && email.length > 0 ? email : null;
}

let controller: InternalPiiConsentController | null = null;

export function getPiiConsentController(): PiiConsentController {
  if (controller === null) {
    controller = createPiiConsentController();
  }

  // Re-attaching each call keeps the window API bound to the live `window` and drains any calls a
  // consent tool buffered against the before-init stub. Draining clears the queue, so repeat calls
  // are no-ops once the real handlers are installed.
  controller.attachWindowApi();

  return controller;
}

function readConsentDecision(): ConsentDecision | null {
  const value = readCookie(consentCookieName);
  return value === optedInValue || value === optedOutValue ? value : null;
}

function drainQueuedConsentCalls(queue: unknown, handlers: { optIn: () => void; optOut: () => void }): unknown[] {
  if (!Array.isArray(queue)) {
    return [];
  }

  const remainingCalls: unknown[] = [];
  for (const entry of queue) {
    if (!isRecord(entry)) {
      remainingCalls.push(entry);
      continue;
    }
    if (entry.name === 'optIn') {
      handlers.optIn();
    } else if (entry.name === 'optOut') {
      handlers.optOut();
    } else {
      remainingCalls.push(entry);
    }
  }

  return remainingCalls;
}

export function attachIdentifyWindowApi(identify: (email: string) => void): void {
  if (typeof window === 'undefined') {
    return;
  }

  const namespace: Record<string, unknown> = isRecord(window.mutiny) ? window.mutiny : {};
  const client: Record<string, unknown> = isRecord(namespace.client) ? namespace.client : { _queue: [] };
  const queue = client._queue;

  client.identify = (userIdOrEmail: unknown, info: unknown) => {
    const email = resolveIdentifyEmail(userIdOrEmail, info);
    if (email !== null) {
      identify(email);
    }
  };

  if (Array.isArray(queue)) {
    const remainingCalls: unknown[] = [];
    for (const entry of queue) {
      if (!isRecord(entry) || entry.name !== 'identify' || !Array.isArray(entry.args)) {
        remainingCalls.push(entry);
        continue;
      }

      const [userIdOrEmail, info] = entry.args;
      const email = resolveIdentifyEmail(userIdOrEmail, info);
      if (email !== null) {
        identify(email);
      }
    }
    client._queue = remainingCalls;
  }

  namespace.client = client;
  window.mutiny = namespace;
}

function createPiiConsentController(): InternalPiiConsentController {
  const listeners = new Set<ConsentListener>();
  let defaultOptOut = false;
  let piiTrackingEnabled = false;

  const computeEnabled = (): boolean => {
    const decision = readConsentDecision();
    if (decision === optedInValue) {
      return true;
    }
    if (decision === optedOutValue) {
      return false;
    }

    return !defaultOptOut;
  };

  const setEnabled = (nextEnabled: boolean): void => {
    if (nextEnabled === piiTrackingEnabled) {
      return;
    }

    piiTrackingEnabled = nextEnabled;
    for (const listener of listeners) {
      listener(piiTrackingEnabled);
    }
  };

  const optIn = (): void => {
    writeCookie(consentCookieName, optedInValue);
    setEnabled(true);
  };

  const optOut = (): void => {
    writeCookie(consentCookieName, optedOutValue);
    setEnabled(false);
  };

  const installWindowApi = (): void => {
    if (typeof window === 'undefined') {
      return;
    }

    const namespace: Record<string, unknown> = isRecord(window.mutiny) ? window.mutiny : {};
    const client: Record<string, unknown> = isRecord(namespace.client) ? namespace.client : { _queue: [] };
    const queue = client._queue;

    client.optIn = () => optIn();
    client.optOut = () => optOut();

    // Replay any calls a consent tool made against the before-init stub in the order they were made,
    // so a buffered opt-out after an opt-in (or vice versa) resolves to the visitor's last choice.
    client._queue = drainQueuedConsentCalls(queue, { optIn, optOut });

    namespace.client = client;
    window.mutiny = namespace;
  };

  return {
    attachWindowApi: installWindowApi,
    resolveEnabled: (defaultOptOutPiiTracking: boolean): boolean => {
      defaultOptOut = defaultOptOutPiiTracking;
      piiTrackingEnabled = computeEnabled();
      return piiTrackingEnabled;
    },
    isPiiTrackingEnabled: (): boolean => piiTrackingEnabled,
    subscribe: (listener: ConsentListener): (() => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
